export type VerticalId =
  | 'oral'
  | 'uro'
  | 'prostate'
  | 'skin'
  | 'breast'
  | 'lung'
  | 'colorectal'
  | 'gyn'
  | 'heme'
  | 'neuro'
  | 'pancreas'
  | 'liver'
  | 'gastric'
  | 'thyroid'
  | 'sarcoma'
  | 'general';

export type VerticalMeta = {
  id: VerticalId;
  name: string;
  title: string;
  specialty: string;
  blurb: string;
  keywords: string[];
};

export const VERTICALS: VerticalMeta[] = [
  {
    id: 'oral',
    name: 'Oral / Head & Neck',
    title: 'ByOnco Oral',
    specialty: 'Head & Neck / Oral Oncology',
    blurb: 'Dental and oral medicine into head and neck oncology, with lesion capture and urgency rules.',
    keywords: ['oral', 'head', 'neck', 'tongue', 'ulcer'],
  },
  {
    id: 'uro',
    name: 'Urologic',
    title: 'ByOnco Uro',
    specialty: 'Urologic Oncology',
    blurb: 'Hematuria, bladder, kidney, and testicular findings with a complete packet.',
    keywords: ['uro', 'bladder', 'hematuria', 'kidney', 'renal', 'testicular'],
  },
  {
    id: 'prostate',
    name: 'Prostate',
    title: 'ByOnco Prostate',
    specialty: 'Urologic Oncology (Prostate)',
    blurb: 'PSA trend, MRI, and biopsy status for a clean prostate oncology handoff.',
    keywords: ['prostate', 'psa'],
  },
  {
    id: 'skin',
    name: 'Skin / Melanoma',
    title: 'ByOnco Skin',
    specialty: 'Dermatologic / Surgical Oncology (Melanoma)',
    blurb: 'ABCDE lesion capture into derm-onc and surgical oncology.',
    keywords: ['skin', 'melanoma', 'mole', 'abcde'],
  },
  {
    id: 'breast',
    name: 'Breast',
    title: 'ByOnco Breast',
    specialty: 'Breast Oncology',
    blurb: 'Mass, imaging, and BI-RADS into breast oncology with tissue status.',
    keywords: ['breast'],
  },
  {
    id: 'lung',
    name: 'Lung / Thoracic',
    title: 'ByOnco Lung',
    specialty: 'Thoracic Oncology',
    blurb: 'Hemoptysis, nodules, and smoking history into thoracic oncology.',
    keywords: ['lung', 'thoracic', 'hemoptysis'],
  },
  {
    id: 'colorectal',
    name: 'Colorectal',
    title: 'ByOnco Colorectal',
    specialty: 'GI / Colorectal Oncology',
    blurb: 'Rectal bleeding, FIT, and colonoscopy status into GI oncology.',
    keywords: ['colorectal', 'colon', 'rectal', 'crc'],
  },
  {
    id: 'gyn',
    name: 'Gynecologic',
    title: 'ByOnco Gyn',
    specialty: 'Gynecologic Oncology',
    blurb: 'Ovarian, cervical, and endometrial findings into gyn oncology.',
    keywords: ['gyn', 'ovarian', 'cervical', 'endometrial', 'uterine', 'gynecolog'],
  },
  {
    id: 'heme',
    name: 'Hematologic',
    title: 'ByOnco Heme',
    specialty: 'Hematologic Oncology',
    blurb: 'Lymphoma, leukemia, and myeloma capture for heme-onc routing.',
    keywords: ['heme', 'lymphoma', 'leukemia', 'myeloma', 'blood'],
  },
  {
    id: 'neuro',
    name: 'Neuro / CNS',
    title: 'ByOnco Neuro',
    specialty: 'Neuro-Oncology',
    blurb: 'New seizures, focal deficits, and brain lesions into neuro-oncology.',
    keywords: ['neuro', 'brain', 'cns', 'glioma', 'seizure'],
  },
  {
    id: 'pancreas',
    name: 'Pancreatic',
    title: 'ByOnco Pancreas',
    specialty: 'GI / Pancreatic Oncology',
    blurb: 'Painless jaundice, mass, and CA 19-9 into pancreatic oncology.',
    keywords: ['pancrea', 'jaundice'],
  },
  {
    id: 'liver',
    name: 'Liver / HCC',
    title: 'ByOnco Liver',
    specialty: 'Hepatobiliary Oncology',
    blurb: 'Cirrhosis, AFP, and LI-RADS findings into hepatobiliary oncology.',
    keywords: ['liver', 'hcc', 'hepat'],
  },
  {
    id: 'gastric',
    name: 'Gastric / Esophageal',
    title: 'ByOnco Upper GI',
    specialty: 'GI / Foregut Oncology',
    blurb: 'Dysphagia, weight loss, and endoscopy status into foregut oncology.',
    keywords: ['gastric', 'stomach', 'esophag', 'gerd'],
  },
  {
    id: 'thyroid',
    name: 'Thyroid',
    title: 'ByOnco Thyroid',
    specialty: 'Endocrine / Head & Neck Oncology',
    blurb: 'Nodules, FNA, and Bethesda category into endocrine oncology.',
    keywords: ['thyroid'],
  },
  {
    id: 'sarcoma',
    name: 'Sarcoma / Bone',
    title: 'ByOnco Sarcoma',
    specialty: 'Sarcoma / Pediatric Oncology',
    blurb: 'Persistent bone pain and soft-tissue masses into sarcoma pathways.',
    keywords: ['sarcoma', 'bone pain', 'ewing'],
  },
  {
    id: 'general',
    name: 'General / Other',
    title: 'ByOnco General',
    specialty: 'Medical Oncology (general)',
    blurb: 'Suspected cancer when the first-mile pathway is still unclear.',
    keywords: ['other', 'suspected', 'general'],
  },
];

export const VERTICAL_BY_ID: Record<string, VerticalMeta> = Object.fromEntries(
  VERTICALS.map((v) => [v.id, v])
);

export function inferVerticalId(cancerType: string, hint = ''): VerticalId {
  const raw = `${hint} ${cancerType}`.toLowerCase();
  if (hint && VERTICAL_BY_ID[hint]) return hint as VerticalId;
  const ordered = VERTICALS.filter((v) => v.id !== 'general');
  for (const v of ordered) {
    if (v.keywords.some((k) => raw.includes(k))) return v.id;
  }
  return 'general';
}
